import React, { useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Stack,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  FormLabel,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

import AreaSelector from "../AreaSelector";
import CustomButton from "../CustomButton";
import colors from "../../constants/colors";
import { updateSafeGapAPI } from "../../apis/metrics.apis";

const SafeGapGridDataChange = () => {
  const [level, setLevel] = useState("city"); // 'city' or 'national'
  const [selectedArea, setSelectedArea] = useState({});
  const [entries, setEntries] = useState([{ lower_bound: "", upper_bound: "", safe_gap: "",
        lane1: "",
    lane2: "",
    lane3: "",
    lane4: "",
    lane5: "",
    lane6: "",
    lane7: "",
   }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAreaChange = (value) => setSelectedArea(value);

  const handleEntryChange = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);
  };

  const addNewEntry = () => setEntries([...entries, { lower_bound: "", upper_bound: "", safe_gap: "" }]);

  const removeEntry = (index) => {
    const updated = [...entries];
    updated.splice(index, 1);
    setEntries(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      level === "city" &&
      (!selectedArea?.zone?.id || !selectedArea?.state?.id || !selectedArea?.city?.id)
    ) {
      toast.error("Please select Zone, State, and City.");
      return;
    }

    if (entries.some((e) => !e.lower_bound || !e.upper_bound || !e.safe_gap)) {
      toast.error("Please fill all the fields.");
      return;
    }

    const body = {
      city_id: level === "city" ? selectedArea.city.id : null, // null or -1 for national
      entries: entries.map((e) => ({
        lower_bound: parseFloat(e.lower_bound),
        upper_bound: parseFloat(e.upper_bound),
        safe_gap: parseFloat(e.safe_gap),
                lane1: parseFloat(e.lane1),
        lane2: parseFloat(e.lane2),
        lane3: parseFloat(e.lane3),
        lane4: parseFloat(e.lane4),
        lane5: parseFloat(e.lane5),
        lane6: parseFloat(e.lane6),
        lane7: parseFloat(e.lane7),
      })),
    };

    try {
      setIsSubmitting(true);
      await updateSafeGapAPI(body);
      setSelectedArea({});
      setEntries([{ lower_bound: "", upper_bound: "", safe_gap: "", lane1:"",
        lane2:"", lane3:"", lane4:"", lane5:"", lane6:"", lane7:"",
       }]);
      toast.success("Safe gap data submitted!");
    } catch (error) {
      toast.error("Submission failed!");
    } finally {
      setIsSubmitting(false);
    }
  };

 return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={4}>
        {/* Level Selection */}
        <Grid item xs={12}>
          <FormLabel component="legend">Scope</FormLabel>
          <RadioGroup row value={level} onChange={(e) => setLevel(e.target.value)}>
            <FormControlLabel value="city" control={<Radio />} label="City Level" />
            <FormControlLabel value="national" control={<Radio />} label="National Level" />
          </RadioGroup>
        </Grid>

        {/* Area Selector (only for city-level) */}
        {level === "city" && (
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6">Select Area</Typography>
            <AreaSelector onChange={handleAreaChange} />
          </Grid>
        )}

        {/* Safe Gap & Lane Occupancy Fields */}
        <Grid item xs={12} sm={6} md={level === "city" ? 8 : 12}>
          <Typography variant="h6" mb={2}>
            Safe Gap Data
          </Typography>
          {entries.map((entry, index) => (
            // Using Box to group each entry visually
            <Box
              key={index}
              mb={3}
              p={2}
              sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Lower Bound"
                    type="number"
                    fullWidth
                    value={entry.lower_bound}
                    onChange={(e) =>
                      handleEntryChange(index, "lower_bound", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Upper Bound"
                    type="number"
                    fullWidth
                    value={entry.upper_bound}
                    onChange={(e) =>
                      handleEntryChange(index, "upper_bound", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Safe Gap"
                    type="number"
                    fullWidth
                    value={entry.safe_gap}
                    onChange={(e) =>
                      handleEntryChange(index, "safe_gap", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={addNewEntry}
                      sx={{ backgroundColor: colors.PRIMARY }}
                    >
                      <AddIcon />
                    </IconButton>
                    {entries.length > 1 && (
                      <IconButton
                        onClick={() => removeEntry(index)}
                        sx={{ backgroundColor: colors.PRIMARY }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>

              {/* Lane Occupancy Fields */}
              <Typography variant="subtitle1" mt={3} mb={2}>
                Lane Occupancy (%)
              </Typography>
              <Grid container spacing={2}>
                {Array.from({ length: 7 }, (_, i) => i + 1).map((laneNum) => (
                  <Grid item xs={4} sm={3} md={3} key={laneNum}>
                    <TextField
                      size="small"
                      label={`Lane ${laneNum} %`}
                      type="number"
                      fullWidth
                      value={entry[`lane${laneNum}`]}
                      onChange={(e) =>
                        handleEntryChange(index, `lane${laneNum}`, e.target.value)
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Grid>
      </Grid>

      <Stack mt={4} direction="row" justifyContent="center">
        <CustomButton type="submit" disabled={isSubmitting} sx={{ minWidth: "200px" }}>
          Submit
        </CustomButton>
      </Stack>
    </form>
  );
};

export default SafeGapGridDataChange;